/**
 * One line of the entry form (batch 8 T12c3, #3033): the PO line chosen from its number, SKU and name, the quantity (signed), the
 * exact money fields, the explicit receipt allocations of a goods line and the explicit return links of a credit line, chosen from
 * the facts by number and date, never typed as ids. Nothing is derived or defaulted on the user's behalf except the description of a
 * freshly chosen PO line; the server's counters are shown beside the choices, not recomputed.
 */

import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, IconButton, Paper, Stack, TextField, Tooltip, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import type { FactsLine } from '@/hooks/useSupplierInvoices';
import { MAX_LINKS_PER_LINE, newLocalId, removedStillApplies, type DraftErrors, type LineDraft, type LinkDraft, type RemovedLinks } from './invoiceDraft';
import { parseSignedInteger } from './money';
import { NS, poLineLabel, useFormatters } from './invoiceView';

interface Props {
  line: LineDraft;
  index: number;
  poLines: readonly FactsLine[];
  errors: DraftErrors;
  showErrors: boolean;
  disabled: boolean;
  canRemove: boolean;
  /** Links the form removed when the sign of the quantity changed (the user is told, nothing hidden is sent). */
  removedLinks?: RemovedLinks | undefined;
  onChange: (patch: Partial<LineDraft>) => void;
  onRemove: () => void;
}

export function InvoiceLineEditor({ line, index, poLines, errors, showErrors, disabled, canRemove, removedLinks, onChange, onRemove }: Props) {
  const { t } = useTranslation(NS);
  const fmt = useFormatters();
  const at = `lines.${String(index)}`;
  const poLine = poLines.find((candidate) => candidate.id === line.poLineId);
  const quantity = parseSignedInteger(line.quantity, 1_000_000);
  const isGoods = quantity.ok && quantity.value > 0;
  const isCredit = quantity.ok && quantity.value < 0;

  const error = (path: string): { error: boolean; helperText?: string } => {
    const code = showErrors ? errors[path] : undefined;
    return code ? { error: true, helperText: t(`errors.field.${code}`) } : { error: false };
  };
  const field = (name: string) => `${at}.${name}`;

  const setLinks = (key: 'allocations' | 'creditLinks', links: LinkDraft[]) => onChange({ [key]: links } as Partial<LineDraft>);

  const linkRows = (
    key: 'allocations' | 'creditLinks',
    path: string,
    options: { id: string; label: string }[],
    labels: { refLabel: string; quantityLabel: string; add: string; title: string; hint: string },
  ) => (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="subtitle2">{labels.title}</Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        {labels.hint}
      </Typography>
      <Stack spacing={1}>
        {line[key].map((link, linkIndex) => {
          const base = `${at}.${path}.${String(linkIndex)}`;
          return (
            <Stack key={link.id} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
              <TextField
                select
                size="small"
                label={labels.refLabel}
                value={link.refId}
                disabled={disabled}
                onChange={(event) => setLinks(key, line[key].map((candidate) => (candidate.id === link.id ? { ...candidate, refId: event.target.value } : candidate)))}
                slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
                sx={{ flex: 2, minWidth: 220 }}
                {...error(`${base}.ref`)}
              >
                <option value="">{t('form.choose')}</option>
                {options.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </TextField>
              <TextField
                size="small"
                label={labels.quantityLabel}
                value={link.quantity}
                disabled={disabled}
                onChange={(event) => setLinks(key, line[key].map((candidate) => (candidate.id === link.id ? { ...candidate, quantity: event.target.value } : candidate)))}
                slotProps={{ htmlInput: { inputMode: 'numeric' } }}
                sx={{ flex: 1, minWidth: 100 }}
                {...error(`${base}.quantity`)}
              />
              <Tooltip title={t('form.removeLink')}>
                <span>
                  <IconButton
                    aria-label={t('form.removeLink')}
                    disabled={disabled}
                    onClick={() => setLinks(key, line[key].filter((candidate) => candidate.id !== link.id))}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          );
        })}
      </Stack>
      <Button
        size="small"
        startIcon={<AddIcon />}
        disabled={disabled || line[key].length >= MAX_LINKS_PER_LINE}
        onClick={() => setLinks(key, [...line[key], { id: newLocalId(), refId: '', quantity: '' }])}
        sx={{ mt: 1 }}
      >
        {labels.add}
      </Button>
    </Box>
  );

  const receiptOptions = (poLine?.receiptLines ?? []).map((receipt) => ({
    id: receipt.id,
    label: t('form.receiptOption', {
      number: receipt.receiptNumber ?? t('form.unnumbered'),
      date: receipt.receivedAt ? fmt.date(receipt.receivedAt) : '–',
      accepted: receipt.quantityAccepted,
      allocated: receipt.allocatedLive,
    }),
  }));
  const returnOptions = (poLine?.returnLines ?? []).map((ret) => ({
    id: ret.id,
    label: t('form.returnOption', { number: ret.returnNumber ?? t('form.unnumbered'), quantity: ret.quantity, credited: ret.creditedLive }),
  }));

  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid={`line-editor-${String(index)}`} role="group" aria-label={t('form.lineTitle', { number: index + 1 })}>
      <Typography variant="subtitle2" component="h4" sx={{ mb: 1 }}>
        {t('form.lineTitle', { number: index + 1 })}
      </Typography>
      <Stack spacing={1.5}>
        <TextField
          select
          size="small"
          label={t('form.poLine')}
          value={line.poLineId}
          disabled={disabled}
          onChange={(event) => {
            const chosen = poLines.find((candidate) => candidate.id === event.target.value);
            const name = chosen ? [chosen.partSku, chosen.partName].filter((part): part is string => !!part).join(' – ') : '';
            onChange({ poLineId: event.target.value, allocations: [], creditLinks: [], ...(line.description.trim() === '' && name !== '' ? { description: name } : {}) });
          }}
          slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
          helperText={poLine ? t('form.poLineHint', { ordered: poLine.quantityOrdered, received: poLine.quantityReceived }) : t('form.poLineNoneHint')}
        >
          <option value="">{t('form.poLineNone')}</option>
          {poLines.map((candidate) => (
            <option key={candidate.id} value={candidate.id}>
              {poLineLabel(candidate, t)}
            </option>
          ))}
        </TextField>
        <TextField size="small" label={t('form.description')} value={line.description} disabled={disabled} onChange={(event) => onChange({ description: event.target.value })} {...error(field('description'))} />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: 1.5 }}>
          <TextField
            size="small"
            label={t('form.quantity')}
            value={line.quantity}
            disabled={disabled}
            onChange={(event) => onChange({ quantity: event.target.value })}
            slotProps={{ htmlInput: { inputMode: 'numeric' } }}
            {...(showErrors && errors[field('quantity')] ? error(field('quantity')) : { helperText: t('form.quantityHint') })}
          />
          <TextField
            size="small"
            label={t('form.unitPrice')}
            value={line.unitPrice}
            disabled={disabled}
            onChange={(event) => onChange({ unitPrice: event.target.value })}
            slotProps={{ htmlInput: { inputMode: 'decimal' } }}
            {...(showErrors && errors[field('unitPrice')] ? error(field('unitPrice')) : { helperText: t('form.unitPriceHint') })}
          />
          <TextField size="small" label={t('form.discount')} value={line.discount} disabled={disabled} onChange={(event) => onChange({ discount: event.target.value })} slotProps={{ htmlInput: { inputMode: 'decimal' } }} {...error(field('discount'))} />
          <TextField
            size="small"
            label={t('form.vat')}
            value={line.vat}
            disabled={disabled}
            onChange={(event) => onChange({ vat: event.target.value })}
            slotProps={{ htmlInput: { inputMode: 'decimal' } }}
            {...(showErrors && errors[field('vat')]
              ? error(field('vat'))
              : poLine
                ? { helperText: poLine.recordedVatPercent === null ? t('form.vatRecordedUnknown') : t('form.vatRecorded', { percent: fmt.percent(poLine.recordedVatPercent) }) }
                : {})}
          />
          <TextField
            size="small"
            label={t('form.statedNet')}
            value={line.statedNet}
            disabled={disabled}
            onChange={(event) => onChange({ statedNet: event.target.value })}
            slotProps={{ htmlInput: { inputMode: 'decimal' } }}
            {...(showErrors && errors[field('statedNet')] ? error(field('statedNet')) : { helperText: t('form.statedNetHint') })}
          />
        </Box>
        {removedLinks !== undefined && removedStillApplies(removedLinks, line.quantity) && (
          <Alert severity="info" role="status" data-testid={`links-removed-${String(index)}`}>
            {t(`form.linksRemoved.${removedLinks}`)}
          </Alert>
        )}
        {isGoods && line.poLineId !== '' &&
          linkRows('allocations', 'alloc', receiptOptions, {
            title: t('form.allocations.title'),
            hint: poLine && receiptOptions.length === 0 ? t('form.allocations.noReceipts') : t('form.allocations.hint'),
            refLabel: t('form.allocations.receipt'),
            quantityLabel: t('form.allocations.quantity'),
            add: t('form.allocations.add'),
          })}
        {isCredit && line.poLineId !== '' &&
          linkRows('creditLinks', 'credit', returnOptions, {
            title: t('form.creditLinks.title'),
            hint: poLine && returnOptions.length === 0 ? t('form.creditLinks.noReturns') : t('form.creditLinks.hint'),
            refLabel: t('form.creditLinks.return'),
            quantityLabel: t('form.creditLinks.quantity'),
            add: t('form.creditLinks.add'),
          })}
        <Box>
          <Button size="small" color="inherit" startIcon={<DeleteOutlineIcon />} disabled={disabled || !canRemove} onClick={onRemove}>
            {t('form.removeLine')}
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
}
